import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { Icon } from '@design/components/icon/Icon';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CategoryTabs.module.scss';
import { CategoryType } from './HistorySidebar';

interface Category {
  label: ReactNode;
  value: CategoryType;
}

interface CategoryTabsProps {
  category: CategoryType;
  changeCategory: (category: CategoryType) => void;
}

export const CategoryTabs = ({
  category,
  changeCategory,
}: CategoryTabsProps) => {
  const { t } = useTranslation(['chat']);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  const allCategories: Category[] = [
    {
      label: (
        <div className={styles.categoryLabel}>{t('chat:historyTab.all')}</div>
      ),
      value: 'all',
    },
    {
      label: (
        <div
          className={clsx(
            styles.categoryLabel,
            isScheduledTaskEnabled && styles.iconOnly
          )}
        >
          <Icon
            iconName="Bookmarked"
            size={16}
            fillColor={
              category === 'bookmarked'
                ? 'var(--icon-primary)'
                : 'var(--icon-default)'
            }
          />
          {!isScheduledTaskEnabled && <>{t('chat:historyTab.bookmarked')}</>}
        </div>
      ),
      value: 'bookmarked',
    },
    {
      label: (
        <div className={clsx(styles.categoryLabel, styles.iconOnly)}>
          <Icon iconName="Clock" size={16} fillColor="currentColor" />
        </div>
      ),
      value: 'scheduled',
    },
  ];

  const categories = isScheduledTaskEnabled
    ? allCategories
    : allCategories.filter(cat => cat.value !== 'scheduled');

  const handleCategoryChange = (index: number) => {
    changeCategory(categories[index].value);
  };

  const activeIndex = categories.findIndex(cat => cat.value === category);

  return (
    <div
      className={styles.categoryTabsWrapper}
      onClick={e => e.stopPropagation()}
    >
      <ButtonGroup
        className={styles.styledButtonGroup}
        labels={categories.map(cat => cat.label)}
        activeIndex={activeIndex}
        onClick={handleCategoryChange}
      />
    </div>
  );
};
